import { useCallback, useEffect, useState } from 'react'
import LocalCache from '../../backend/LocalCache'
import EventsEngine from '../../engines/EventsEngine'

export type QueryOptions<T> = {
    queryKey: string
    queryFn: () => T | Promise<T>
    cache?: boolean
    // When condition is true it fetches data or takes from cache
    enabled?: boolean
}

type QueryState<T> = {
    status: QueryStatus
    response: T
}

const useQuery = <T>(options: QueryOptions<T>) => {
    const { queryFn, queryKey, cache, enabled = true } = options

    const [qState, setQState] = useState<QueryState<T>>({
        status: enabled ? 'loading' : 'idle',
        response: null
    })

    const doQuery = useCallback(async () => {
        if (!enabled) return

        try {
            setQState({ status: 'loading', response: null })

            const call = async () => {
                const res = await queryFn()
                if (cache && res) LocalCache.set(queryKey, res)
                setQState({ status: 'success', response: res })
            }

            if (cache) {
                const cacheEntry = LocalCache.get(queryKey)
                if (cacheEntry) {
                    if (cacheEntry.stale) {
                        setQState({ status: 'stale', response: cacheEntry.value })
                        await call()
                        return
                    } else {
                        setQState({ status: 'success', response: cacheEntry.value })
                        return
                    }
                } else {
                    await call()
                }
            } else {
                await call()
            }
        } catch (e) {
            console.log('Error was caught and handled in useQuery')
            console.error(e)
            setQState({ status: 'error', response: null })
        }
    }, [queryKey, enabled])

    const refetch = useCallback(async () => {
        if (!enabled) {
            setQState({ status: 'idle', response: null })
            return
        }

        if (cache)
            LocalCache.delete(queryKey)

        await doQuery()
    }, [queryKey, doQuery, enabled])

    const resetWith = useCallback((data: T) => {
        if (!enabled) {
            setQState({ status: 'idle', response: null })
            return
        }

        if (cache) {
            LocalCache.delete(queryKey)
            LocalCache.set(queryKey, data)
        }

        setQState(prev => ({
            ...prev,
            status: 'success',
            response: data
        }))
    }, [queryKey, enabled])

    useEffect(() => {
        if (!enabled) {
            setQState({ status: 'idle', response: null })
            return
        }

        (async () => await doQuery())()
    }, [queryKey, enabled])

    useEffect(() => {
        if (!enabled) {
            setQState({ status: 'idle', response: null })
            return
        }

        const cleanup = EventsEngine.subscribe<{ queryKey: string }>(
            'query-cache-stale',
            'use-query-' + queryKey,
            args => {
                if (queryKey === args.queryKey) doQuery()
            }
        )
        return () => cleanup()
    }, [queryKey, enabled])

    return { ...qState, refetch, resetWith }
}

export default useQuery

export type QueryStatus = 'idle' | 'loading' | 'stale' | 'success' | 'error'

export const buildQueryKey = (...deps: (string | number)[]) => (deps ?? []).join('-')
